// utils/transactionHooks.js
import DoubleEntryService from "./doubleEntryService";

/**
 * Transaction Hooks for Automatic Double-Entry Recording
 *
 * These functions should be called automatically whenever financial transactions occur.
 * They bridge the existing transaction system with the new double-entry accounting.
 */

export class TransactionHooks {
  /**
   * Hook: After loan disbursement
   * Call this after a loan is successfully disbursed
   */
  static async onLoanDisbursed({
    loanId,
    amount,
    date,
    company,
    token,
    accountId
  }) {
    try {
      console.log(`[Accounting Hook] Recording loan disbursement: Loan ${loanId}, Amount: ${amount}`);

      await DoubleEntryService.recordLoanDisbursement({
        loanId,
        amount,
        date: date || new Date(),
        company,
        token,
        accountId,
      });

      console.log(`[Accounting Hook] ✓ Loan disbursement recorded successfully`);
      return { success: true };
    } catch (error) {
      console.error(`[Accounting Hook] ✗ Failed to record loan disbursement:`, error);
      // Don't throw - we don't want to block the main transaction
      return { success: false, error: error.message };
    }
  }

  /**
   * Hook: After loan repayment
   * Call this after a repayment is successfully processed
   */
  static async onLoanRepayment({
    loanId,
    principalAmount,
    interestAmount,
    date,
    company,
    token,
    accountId
  }) {
    try {
      console.log(`[Accounting Hook] Recording loan repayment: Loan ${loanId}, Principal: ${principalAmount}, Interest: ${interestAmount}`);

      await DoubleEntryService.recordLoanRepayment({
        loanId,
        principalAmount: principalAmount || 0,
        interestAmount: interestAmount || 0,
        date: date || new Date(),
        company,
        token,
        accountId,
      });

      console.log(`[Accounting Hook] ✓ Loan repayment recorded successfully`);
      return { success: true };
    } catch (error) {
      console.error(`[Accounting Hook] ✗ Failed to record loan repayment:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Hook: After fee collection (admin fees, GPS fees, penalties)
   * Call this after a fee is successfully collected
   */
  static async onFeeCollected({
    feeType,
    amount,
    date,
    company,
    token,
    loanId,
    accountId
  }) {
    try {
      console.log(`[Accounting Hook] Recording fee collection: ${feeType}, Amount: ${amount}`);

      await DoubleEntryService.recordFeeIncome({
        feeType,
        amount,
        date: date || new Date(),
        company,
        token,
        loanId,
        accountId,
      });

      console.log(`[Accounting Hook] ✓ Fee collection recorded successfully`);
      return { success: true };
    } catch (error) {
      console.error(`[Accounting Hook] ✗ Failed to record fee collection:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Hook: After expense is recorded
   * Call this after an operational expense is recorded
   */
  static async onExpenseRecorded({
    amount,
    description,
    date,
    company,
    token,
    accountId
  }) {
    try {
      console.log(`[Accounting Hook] Recording expense: ${description}, Amount: ${amount}`);

      await DoubleEntryService.recordExpense({
        amount,
        description,
        date: date || new Date(),
        company,
        token,
        accountId,
      });

      console.log(`[Accounting Hook] ✓ Expense recorded successfully`);
      return { success: true };
    } catch (error) {
      console.error(`[Accounting Hook] ✗ Failed to record expense:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Hook: After float addition
   * Call this after float/capital is added to an account
   */
  static async onFloatAdded({
    amount,
    date,
    company,
    token,
    accountId,
    source
  }) {
    try {
      console.log(`[Accounting Hook] Recording float addition: Amount: ${amount}, Source: ${source}`);

      await DoubleEntryService.recordFloatAddition({
        amount,
        date: date || new Date(),
        company,
        token,
        accountId,
        source: source || "Owner",
      });

      console.log(`[Accounting Hook] ✓ Float addition recorded successfully`);
      return { success: true };
    } catch (error) {
      console.error(`[Accounting Hook] ✗ Failed to record float addition:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Hook: After investment deposit
   */
  static async onInvestmentDeposit({
    investmentId,
    amount,
    date,
    company,
    token,
    accountId,
  }) {
    try {
      console.log(`[Accounting Hook] Recording investment deposit: Investment ${investmentId}, Amount: ${amount}`);

      await DoubleEntryService.recordInvestmentDeposit({
        investmentId,
        amount,
        date: date || new Date(),
        company,
        token,
        accountId,
      });

      console.log(`[Accounting Hook] ✓ Investment deposit recorded successfully`);
      return { success: true };
    } catch (error) {
      console.error(`[Accounting Hook] ✗ Failed to record investment deposit:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Hook: After investment payout
   */
  static async onInvestmentPayout({
    investmentId,
    principalAmount,
    interestAmount,
    withholdingTaxAmount,
    date,
    company,
    token,
    accountId,
  }) {
    try {
      console.log(`[Accounting Hook] Recording investment payout: Investment ${investmentId}, Principal: ${principalAmount}, Interest: ${interestAmount}`);

      await DoubleEntryService.recordInvestmentPayout({
        investmentId,
        principalAmount: principalAmount || 0,
        interestAmount: interestAmount || 0,
        withholdingTaxAmount: withholdingTaxAmount || 0,
        date: date || new Date(),
        company,
        token,
        accountId,
      });

      console.log(`[Accounting Hook] ✓ Investment payout recorded successfully`);
      return { success: true };
    } catch (error) {
      console.error(`[Accounting Hook] ✗ Failed to record investment payout:`, error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Batch process existing transactions (for initial setup)
   * This is useful when you're adding double-entry to an existing system
   */
  static async processExistingTransactions({ company, token, transactions }) {
    const results = {
      processed: 0,
      failed: 0,
      errors: [],
    };

    for (const txn of transactions) {
      try {
        const { transaction_type, amount, transaction_date, loan, account, from_account, to_account } = txn;

        // Determine which hook to call based on transaction type
        switch (transaction_type) {
          case "Loan Disbursement":
            if (loan && from_account) {
              await this.onLoanDisbursed({
                loanId: loan,
                amount,
                date: transaction_date,
                company,
                token,
                accountId: from_account,
              });
              results.processed++;
            }
            break;

          case "Loan Repayment":
            if (loan && (to_account || account)) {
              // You'll need to split principal and interest
              // For now, we'll treat it as all principal
              await this.onLoanRepayment({
                loanId: loan,
                principalAmount: amount,
                interestAmount: 0,
                date: transaction_date,
                company,
                token,
                accountId: to_account || account,
              });
              results.processed++;
            }
            break;

          case "Admin Fees":
          case "GPS Fee Payment":
          case "Penalty Fee":
            if (loan && (to_account || account)) {
              await this.onFeeCollected({
                feeType: transaction_type,
                amount,
                date: transaction_date,
                company,
                token,
                loanId: loan,
                accountId: to_account || account,
              });
              results.processed++;
            }
            break;

          case "Operational Cost":
            if (from_account || account) {
              await this.onExpenseRecorded({
                amount,
                description: txn.notes || "Operational expense",
                date: transaction_date,
                company,
                token,
                accountId: from_account || account,
              });
              results.processed++;
            }
            break;

          case "Float Addition":
            if (to_account || account) {
              await this.onFloatAdded({
                amount,
                date: transaction_date,
                company,
                token,
                accountId: to_account || account,
                source: "Owner",
              });
              results.processed++;
            }
            break;

          default:
            console.log(`[Accounting Hook] Skipping transaction type: ${transaction_type}`);
        }
      } catch (error) {
        results.failed++;
        results.errors.push({
          transaction: txn.id,
          error: error.message,
        });
      }
    }

    return results;
  }
}

export default TransactionHooks;
